import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import type {ReccoTopicDTO} from '@/ts/types/dto/recco/topic.dto';
import type {ReccoChallengeStateDTO} from '@/ts/types/dto/recco/challenge';
import type {ComponentContent} from '@/ts/types/dto/interventions-dto';

/** Media type facet — set on educational library items only (challenges/programs have none) */
export enum LibraryMediaTypeDTO {
  Watch = 'watch',
  Read = 'read',
  Listen = 'listen',
}

/** Content type facet — every library item is exactly one of these */
export enum LibraryContentTypeDTO {
  Challenge = 'challenge',
  Program = 'program',
  Educational = 'educational',
}

/** Challenge preview carried by a library item (mirrors the BE ChallengePreview) */
export interface LibraryChallengeDTO {
  id: number;
  title: string;
  description?: string | null;
  imageUrl?: string | null;
  hoursDuration?: number | null;
  taskCount?: number | null;
  challengeState?: ReccoChallengeStateDTO | null;
  // 0–1 fraction; drives the progress bar + Resume once the challenge is started (IPD-2943).
  pctCompletionProgress?: number | null;
}

/** Program carried by a library item */
export interface LibraryProgramDTO {
  id: UuidDTO;
  name?: string | null;
  // Absolute URL — the BE derives it from the program's first intervention (programs have no image of their own).
  imageUrl?: string | null;
  interventionIds: UuidDTO[];
}

/**
 * One entry of the Explore library. Exactly one of content/challenge/program is set,
 * matching contentType. `recommended` drives the "Recommended" pill per item.
 */
export interface ExploreLibraryItemDTO {
  contentType: LibraryContentTypeDTO;
  /**
   * The item's single media type — educational items only, and what the Read / Listened / Watched
   * marker and the play badge are keyed off (together with `consumed`). Null on challenges and
   * programs: see `mediaTypes` for those.
   */
  mediaType?: LibraryMediaTypeDTO | null;
  /**
   * Every format the item carries, and what the media-type facet filters on server-side. One
   * element for educational items, one per marked format for a challenge, empty for programs.
   * It decides whether a challenge appears under a media chip, and in a flat collection (the topic
   * grid) a challenge takes its first format as the card's media label, since nothing there supplies
   * the type the way a rail heading does.
   */
  mediaTypes?: LibraryMediaTypeDTO[];
  /**
   * Every topic the item is tagged with — an item belongs to all of them, with no winner
   * (IPD-2936). This is what topic rails group on.
   *
   * The BE always sends it (defaults to an empty list, and NON_NULL only strips nulls), but
   * nothing validates responses here, so readers treat it as optional like every other field.
   */
  topics?: ReccoTopicDTO[];
  /**
   * @deprecated The BE's first match among `topics`, kept only while it finishes retiring the
   * field. Never group on it: for content tagged both mental wellbeing and relaxation it always
   * resolves to the former, which is why the Relaxation rail was empty (IPD-3032).
   */
  topic?: ReccoTopicDTO | null;
  recommended: boolean;
  // True once the user has opened the item (VIEW engagement) — drives the Read/Listened/Watched marker (IPD-2943).
  consumed?: boolean | null;
  content?: ComponentContent | null;
  challenge?: LibraryChallengeDTO | null;
  program?: LibraryProgramDTO | null;
}

/** A partner whose pack is available to the user's org — feeds the "by {Partner}" chips */
export interface ExploreLibraryPartnerDTO {
  id: UuidDTO;
  name?: string | null;
  imageUrl?: string | null;
}

/**
 * The §2 facet selection. OR within each set, AND across sets; an empty set means the
 * facet is inactive. Not persisted — resets on reload (no URL state) by design.
 */
export interface ExploreLibraryFilters {
  mediaTypes: LibraryMediaTypeDTO[];
  topics: ReccoTopicDTO[];
  contentTypes: LibraryContentTypeDTO[];
  saved: boolean;
  /** Narrows to what the recommendation engine recommends for this user — the "For you" set. */
  recommended: boolean;
  partnerIds: UuidDTO[];
}

/**
 * One page of the library. `totalElements` is the pre-window match count, so it is what result
 * counts and "showing N of M" must read — `items.length` is bounded by the requested page size.
 */
export interface ExploreLibraryPage {
  items: ExploreLibraryItemDTO[];
  totalElements: number;
  page: number;
}
